/* eslint-disable jsx-a11y/no-autofocus */
/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtom, useStateXAtomSetter } from '@cloudio-saas/statex';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useRecordId } from 'components/DataStoreReference/DataStoreReference';
import zIndex from 'constants/ZIndex';
import { MotionBox } from 'custom/Motion';
import useHandleClickOutside from 'hooks/useHandleClickOutside';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Editor,
  Element as SlateElement,
  Location,
  Range,
  Transforms,
} from 'slate';
import { ReactEditor, useSlate, useFocused } from 'slate-react';
import { useTheme } from 'theme/Theme';
import { Portal } from './Components';
import { BlockType, MarkType } from './SlateTypes';
import {
  floatingActionPositionAtom,
  isBlockActive,
  isMarkActive,
  setMarkStyleForColor,
  toggleBlock,
  toggleMark,
  useSlateContext,
} from './SlateUtils';
import TextStyle from './RichTextEditorTextStyle';

const useStyles = makeStyles((theme: Theme) => ({
  toolBarRoot: {
    display: 'flex',
    border: '1px solid #D6D4D4',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#444',
    padding: '6px',
  },
}));
export const HoveringToolbar = ({
  staticToolbar,
}: {
  staticToolbar?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const editor = useSlate();
  const { selection } = editor;
  const theme = useTheme();
  const [pos, setPos] = useStateXAtom(floatingActionPositionAtom);
  const classes = useStyles();
  const [textBackgroundColor, setTextBackgroundColor] = useState('#fffff');
  const [textColor, setTextColor] = useState('000000');

  const onClickMarkBackGroundColor = (color: string) => {
    setTextBackgroundColor(color);
    setMarkStyleForColor(editor, 'backgroundColor', color);
  };

  const onClickMarkTextColor = (color: string) => {
    setTextColor(color);
    setMarkStyleForColor(editor, 'color', color);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (pos.linkPopoverVisible) return;
    const el = ref.current;
    const domSelection = window.getSelection();
    if (domSelection && el) {
      if (
        !(selection && ReactEditor.isFocused(editor)) ||
        Range.isCollapsed(selection) ||
        Editor.string(editor, selection) === ''
      ) {
        el.removeAttribute('style');
        setPos((_pos) => ({
          ..._pos,
          toolbarVisible: false,
        }));
      } else {
        const domRange = domSelection.getRangeAt(0);
        const rect = domRange.getBoundingClientRect();
        let top = rect.top + window.pageYOffset - el.offsetHeight;
        let left =
          rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2;
        if (left < 0) left = 8;
        if (top < 0) top = 8;
        el.style.opacity = '1';
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
        setPos((_pos) => ({
          ..._pos,
          toolbarVisible: true,
        }));
      }
    }
  });

  const DefaultItems = () => {
    return (
      <>
        <FormatButton tip="Bold" format="bold" icon="bold" />
        <FormatButton tip="Italic" format="italic" icon="italic" />
        <FormatButton tip="Underline" format="underline" icon="underline" />
        <FormatButton
          tip="Strike Through"
          format="strikethrough"
          icon="strikethrough"
        />
        <FormatButton tip="Code" format="code" icon="code" />
        <Divider
          orientation="vertical"
          sx={{
            borderColor:
              theme.palette.mode !== 'dark' ? '#666' : theme.palette.divider,
          }}
          flexItem
        />
        <FormatButton tip="Ordered List" block="ol" icon="list-ol" />
        <FormatButton tip="Unordered List" block="ul" icon="list-ul" />
        <FormatButton tip="Blockquote" block="quote" icon="quote-left" />
        <Divider
          orientation="vertical"
          sx={{
            borderColor:
              theme.palette.mode !== 'dark' ? '#666' : theme.palette.divider,
          }}
          flexItem
        />
      </>
    );
  };

  return staticToolbar ? (
    <div className={classes.toolBarRoot}>
      <HoveringAction staticToolbar={staticToolbar} />
      <Divider
        orientation="vertical"
        sx={{
          marginLeft: '8px',
          borderColor:
            theme.palette.mode !== 'dark' ? '#666' : theme.palette.divider,
        }}
        flexItem
      />
      <TextStyle
        textColor={textColor}
        textBackgroundColor={textBackgroundColor}
        handleChangeCompletebckGrnd={onClickMarkBackGroundColor}
        handleChangeComplete={onClickMarkTextColor}
      />
      <DefaultItems />
      <LinkBtn />
    </div>
  ) : (
    <Portal>
      <Box
        ref={ref}
        sx={{
          padding: '8px 7px 6px',
          display: 'flex',
          position: 'absolute',
          zIndex: zIndex.Model,
          top: '-10000px',
          left: '-10000px',
          marginTop: '-6px',
          opacity: 0,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#444',
          borderRadius: '4px',
          transition: 'opacity 0.75s',
        }}
      >
        <DefaultItems />
        <LinkBtn />
      </Box>
    </Portal>
  );
};

const LinkBtn = () => {
  const ref = useRef<HTMLDivElement>(null);
  const editor = useSlate();
  const ctx = useSlateContext();
  const theme = useTheme();
  const blockActive = isBlockActive(editor, 'a');
  const [url, setUrl] = useState('');
  const [open, setOpen] = useState(false);
  const setPos = useStateXAtomSetter(floatingActionPositionAtom);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useHandleClickOutside(
    ref,
    (e) => {
      setOpen(false);
      setPos((_pos) => ({
        ..._pos,
        linkPopoverVisible: false,
      }));
    },
    open,
  );

  return (
    <>
      <Box
        data-tip={blockActive ? 'Unlink' : 'Link'}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (blockActive) {
            Transforms.unwrapNodes(editor, {
              match: (n) => SlateElement.isElement(n) && n.type === 'a',
            });
          } else {
            setOpen(true);
            setPos((_pos) => ({
              ..._pos,
              linkPopoverVisible: true,
            }));
          }
        }}
        ref={buttonRef}
        sx={{
          cursor: 'pointer',
          padding: 1,
          margin: 0,
          height: 32,
        }}
      >
        <Icon
          color={blockActive ? 'primary' : '#fff'}
          iconName={blockActive ? 'unlink' : 'link'}
        />
      </Box>
      {open ? (
        <Paper
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          variant="elevation"
          elevation={3}
          ref={ref}
          sx={{
            position: 'absolute',
            width: '250px',
            borderRadius: '4px',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.background.paper,
            top: buttonRef.current ? buttonRef.current.offsetTop + 38 : 0,
            left: buttonRef.current ? buttonRef.current.offsetLeft : 0,
            zIndex: 1,
            '& > input': {
              border: 0,
              width: '100%',
              padding: '8px',
            },
          }}
        >
          <input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (ctx.lastSelection) {
                  setOpen(false);
                  setUrl('');
                  setPos((_pos) => ({
                    ..._pos,
                    linkPopoverVisible: false,
                  }));
                  Transforms.select(editor, ctx.lastSelection);
                  Transforms.wrapNodes(
                    editor,
                    { type: 'a', url, children: [] },
                    { split: true },
                  );
                  Transforms.collapse(editor, { edge: 'end' });
                }
              }
            }}
            placeholder="Enter URL & Press Enter"
          />
        </Paper>
      ) : null}
    </>
  );
};

const FormatButton = ({
  format,
  block,
  icon,
  tip,
}: {
  format?: MarkType;
  block?: BlockType;
  icon: string;
  tip: string;
}) => {
  const editor = useSlate();
  const markActive = format ? isMarkActive(editor, format) : false;
  const blockActive = block ? isBlockActive(editor, block) : false;
  return (
    <Box
      data-tip={tip}
      onMouseDown={(event) => {
        event.preventDefault();
        if (format) toggleMark(editor, format);
        if (block) toggleBlock(editor, block);
      }}
      sx={{
        cursor: 'pointer',
        padding: 1,
        margin: 0,
        height: 32,
      }}
    >
      <Icon
        color={markActive || blockActive ? 'primary' : '#fff'}
        iconName={icon}
      />
    </Box>
  );
};

export const HoveringAction = ({
  staticToolbar,
}: {
  staticToolbar?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const editor = useSlate();
  const [pos, setPos] = useStateXAtom(floatingActionPositionAtom);
  const theme = useTheme();
  const ctx = useSlateContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const recordId = useRecordId();
  const [top, setTop] = useState(-1);
  const focused = useFocused();
  const [showURLInput, setShowURLInput] = useState(false);

  useLayoutEffect(() => {
    if (staticToolbar) {
      setTop(0);
    } else {
      setTop(-1);
    }
  }, [recordId, setPos, staticToolbar]);

  useLayoutEffect(() => {
    if (staticToolbar) {
      setTop(0);
    } else if (!(showURLInput || focused || anchorEl)) {
      setTop(-1);
    } else {
      setTop(pos.top);
    }
  }, [anchorEl, focused, pos.top, setPos, showURLInput, staticToolbar]);

  useHandleClickOutside(
    menuRef,
    (e) => {
      // prevent slate reacting to this click
      // let the handleClose do it's job
      e.preventDefault();
      e.stopPropagation();
    },
    open,
  );
  const [hover, setHover] = useState(false);
  const sel = editor.selection as Location;
  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setAnchorEl(null);
    if (ctx.lastSelection) {
      Transforms.select(editor, ctx.lastSelection);
    }
  };

  if (!showURLInput && (pos.toolbarVisible || top === -1)) {
    return null;
  }

  function toggleBlk(blk: BlockType) {
    const type = pos.type === blk ? 'p' : blk;
    setPos((p) => ({ ...p, type }));
    Transforms.select(editor, sel);
    toggleBlock(editor, type);
    setAnchorEl(null);
  }

  return (
    <>
      <MotionBox
        initial={false}
        ref={ref}
        onMouseLeave={() => {
          setHover(false);
        }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAnchorEl(ref.current);
          setHover(false);
        }}
        sx={{
          zIndex: zIndex.Model,
          borderWidth: '1px',
          borderColor:
            theme.palette.mode === 'dark' && !anchorEl
              ? theme.palette.text.secondary
              : theme.palette.divider,
          borderStyle: 'solid',
          borderRadius: !staticToolbar ? '50%' : '10%',
          width: 30,
          height: 30,
          position: !staticToolbar ? 'absolute' : 'relative',
          left: !staticToolbar ? 8 : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        style={{
          backgroundColor: anchorEl
            ? theme.palette.primary.main
            : hover
            ? theme.palette.background.default
            : theme.palette.background.paper,
        }}
        animate={{ top }}
      >
        <Icon
          width={18}
          height={18}
          sx={{
            '& > span': {
              fill: anchorEl ? '#fff' : theme.palette.text.secondary,
            },
          }}
          iconName={
            staticToolbar
              ? 'list-dropdown'
              : anchorEl
              ? 'angle-up'
              : 'angle-down'
          }
          color={
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : anchorEl
              ? '#fff'
              : theme.palette.grey[800]
          }
        />
      </MotionBox>
      {open ? (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onMouseDown={(e) => {
            // prevent when clicked on a disabled menu item
            e.stopPropagation();
            e.preventDefault();
          }}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
            ref: menuRef,
          }}
        >
          <MItem
            onClick={() => {
              toggleBlk('h1');
            }}
            active={pos.type === 'h1'}
            title="Heading 1"
            icon="h1"
            shortCut="⌘⇧1"
          />
          <MItem
            onClick={() => {
              toggleBlk('h2');
            }}
            active={pos.type === 'h2'}
            title="Heading 2"
            icon="h2"
            shortCut="⌘⇧2"
          />
          <MItem
            onClick={() => {
              toggleBlk('h3');
            }}
            active={pos.type === 'h3'}
            title="Heading 3"
            icon="h3"
            shortCut="⌘⇧3"
          />
          {pos.emptyLine ? (
            <MItem
              onClick={(e) => {
                handleClose(e);
                setShowURLInput(true);
              }}
              title="Embed Image or Video"
              icon="image"
            />
          ) : (
            <MItem
              onClick={() => {
                toggleBlk('p');
              }}
              active={pos.type === 'p'}
              title="Paragraph"
              icon="paragraph"
            />
          )}
          <MItem
            disabled={isBlockActive(editor, 'quote')}
            onClick={() => {
              toggleBlk('quote');
            }}
            active={pos.type === 'quote'}
            title="Quote"
            icon="quote-left"
            shortCut="⌘]"
          />
          <MItem
            disabled={isBlockActive(editor, 'info')}
            onClick={() => {
              toggleBlk('info');
            }}
            active={pos.type === 'info'}
            title="Info"
            icon="circle-exclamation"
          />
          <MItem
            disabled={isBlockActive(editor, 'success')}
            onClick={() => {
              toggleBlk('success');
            }}
            active={pos.type === 'success'}
            title="Success"
            icon="circle-check"
          />
          <MItem
            disabled={isBlockActive(editor, 'warning')}
            onClick={() => {
              toggleBlk('warning');
            }}
            active={pos.type === 'warning'}
            title="Warning"
            icon="circle-exclamation"
          />
          <MItem
            disabled={isBlockActive(editor, 'error')}
            onClick={() => {
              toggleBlk('error');
            }}
            active={pos.type === 'error'}
            title="Error"
            icon="circle-x"
          />
          <Divider />
          <MItem
            onClick={() => {
              setAnchorEl(null);
              Transforms.removeNodes(editor, { at: sel });
            }}
            title="Delete"
            icon="trash"
          />
        </Menu>
      ) : null}
      {showURLInput ? (
        <URLInput
          key="urlInput"
          onClose={() => {
            setShowURLInput(false);
            if (ctx.lastSelection) {
              Transforms.select(editor, ctx.lastSelection);
            }
          }}
        />
      ) : null}
    </>
  );
};

function MItem({
  active,
  disabled,
  icon,
  onClick,
  shortCut,
  title,
}: {
  active?: boolean;
  disabled?: boolean;
  icon: string;
  onClick: React.MouseEventHandler<HTMLLIElement>;
  shortCut?: string;
  title: string;
}) {
  const theme = useTheme();
  return (
    <MenuItem
      disabled={disabled}
      onClick={onClick}
      sx={{ color: active ? theme.palette.primary.main : undefined }}
    >
      <ListItemIcon sx={{ minWidth: 0 }}>
        <Icon color={active ? 'primary' : undefined} iconName={icon} />
      </ListItemIcon>
      <ListItemText>{title}</ListItemText>
      {shortCut ? (
        <Typography sx={{ pl: 4 }} variant="body2" color="text.secondary">
          {shortCut}
        </Typography>
      ) : null}
    </MenuItem>
  );
}

function URLInput({ onClose }: { onClose: () => void }) {
  const [value, setValue] = useState<BlockType>('img');
  const [url, setUrl] = useState('');
  const [ww] = useWindowSize();
  const ctx = useSlateContext();

  function embed() {
    if (ctx.editor && ctx.lastSelection) {
      onClose();
      Transforms.select(ctx.editor, ctx.lastSelection);
      Transforms.insertNodes(ctx.editor, {
        type: value,
        url,
        children: [{ text: '' }],
      });
    }
  }

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      disableEscapeKeyDown
    >
      <DialogTitle>Enter {value === 'img' ? 'Image' : 'Video'} URL</DialogTitle>
      <DialogContent sx={{ width: Math.min(ww - 80, 600) }}>
        <RadioGroup
          row
          aria-label="embed type"
          value={value}
          onChange={(e) => {
            setValue(e.target.value as 'img' | 'video');
          }}
        >
          <FormControlLabel value="img" control={<Radio />} label="Image" />
          <FormControlLabel value="video" control={<Radio />} label="Video" />
        </RadioGroup>
        <TextField
          autoFocus
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
          }}
          margin="dense"
          label="URL"
          fullWidth
          variant="standard"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={!url} variant="contained" onClick={embed}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
